import { autoUpdate, flip, offset, type Placement, size } from '@floating-ui/dom';
import { safePolygon, useFloating, useHover, useInteractions } from '@floating-ui/react';
import { forwardRef, useCallback, useContext, useEffect, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { Transition, type TransitionStatus } from 'react-transition-group';

import Portal from '#@/plumbing/Portal';
import { CARD_OPENING_TRANSITION_DURATION } from '#@/shared/constants';
import QueryMatchesContext from '#@/shared/contexts/queryMatchesContext';
import { isEnter, isEntered, isExiting } from '#@/shared/utils/transitionStates';

import ManagedLayer, { type ManagedLayerProps } from '../ManagedLayer';
import { DROPDOWN_CARD_OVERLAP_WITH_TRIGGER } from './constants';
import MobileBackground from './MobileBackground';
import Card, { type CardProps, DROPDOWN_CARD_EDGE_OFFSET } from './Styles';

export interface DropdownCardProps extends Pick<ManagedLayerProps, 'onClose' | 'additionalRefs'> {
  isOpen: boolean;
  /**
   * A ref to the trigger element. Not needed when using renderTrigger.
   */
  triggerRef?: HTMLElement | null;
  /**
   * A render prop for the trigger element. This provides proper hover interaction
   * through floating-ui when enableHoverInteraction is true.
   */
  renderTrigger?: (props: { ref: React.Ref<HTMLDivElement> }) => React.ReactNode;
  matchTriggerWidth?: boolean;
  unmountOnExit?: boolean;
  /**
   * This is a prop that is used in <Transition>; we need to set both `in` and `appear` to true
   * in order to add the enter transition when it mounts
   * @see https://reactcommunity.org/react-transition-group/transition
   */
  appear?: boolean;
  fixedWidth?: number;
  maxWidth?: number;
  placement?: Placement;
  mainAxisOffset?: number;
  crossAxisOffset?: number;
  /**
   * Custom z-index value for the dropdown card.
   * Useful for nested menus that need to have different stacking contexts.
   */
  zIndex?: number;
  /**
   * If true, enables hover interactions.
   */
  enableHoverInteraction?: boolean;
  /**
   * Delay in ms before opening the dropdown on hover
   */
  openDelay?: number;
  /**
   * Delay in ms before closing the dropdown on hover out
   */
  closeDelay?: number;
  /**
   * Called when the open state should change due to hover interactions
   */
  onHoverStateChange?: (open: boolean) => void;
  /**
   * Node ID for floating-ui tree integration
   */
  nodeId?: string;
  children: React.ReactNode;
}

const DropdownCard = forwardRef<HTMLDivElement, DropdownCardProps>(
  (
    {
      children,
      isOpen,
      triggerRef,
      renderTrigger,
      matchTriggerWidth = false,
      unmountOnExit = false,
      appear = false,
      fixedWidth,
      maxWidth,
      placement = 'bottom-end',
      mainAxisOffset = -DROPDOWN_CARD_OVERLAP_WITH_TRIGGER,
      crossAxisOffset = 0,
      zIndex,
      enableHoverInteraction = false,
      openDelay = 0,
      closeDelay = 0,
      onHoverStateChange,
      nodeId,
      onClose,
      additionalRefs = [],
      ...props
    },
    ref,
  ) => {
    const { isMobile } = useContext(QueryMatchesContext);
    const transitionNodeRef = useRef();
    const internalTriggerRef = useRef<HTMLDivElement | null>(null);

    // Handler for open state changes coming from floating-ui
    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (enableHoverInteraction && onHoverStateChange) {
          onHoverStateChange(open);
        }
      },
      [enableHoverInteraction, onHoverStateChange],
    );

    const { refs, floatingStyles, context } = useFloating({
      nodeId,
      open: isOpen,
      onOpenChange: handleOpenChange,
      whileElementsMounted: autoUpdate,
      placement,
      strategy: 'fixed',
      middleware: [
        ...[
          matchTriggerWidth &&
            !fixedWidth &&
            size({
              apply({ rects, elements }) {
                requestAnimationFrame(() => {
                  Object.assign(elements.floating.style, {
                    width: `${rects.reference.width}px`,
                  });
                });
              },
            }),
        ],
        offset({ mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }),
        flip({ padding: DROPDOWN_CARD_EDGE_OFFSET }),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            requestAnimationFrame(() => {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
                maxHeight: `${availableHeight}px`,
              });
            });
          },
          padding: DROPDOWN_CARD_EDGE_OFFSET,
        }),
      ],
    });

    // Set up hover interactions with safePolygon
    const hover = useHover(context, {
      enabled: enableHoverInteraction,
      delay: { open: openDelay, close: closeDelay },
      handleClose: safePolygon(),
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    // Synchronize elements with floating-ui context
    useEffect(() => {
      if (triggerRef) {
        refs.setReference(triggerRef);
      } else if (internalTriggerRef.current) {
        refs.setReference(internalTriggerRef.current);
      }
    }, [refs, triggerRef]);

    const cardRef = mergeRefs([refs.setFloating, ref, transitionNodeRef]);
    const renderCard = (state: TransitionStatus, cardProps: Partial<CardProps>) => (
      <Card
        data-cy-dropdown-card-open={isEntered(state)}
        ref={cardRef}
        $isOpen={isEnter(state)}
        $zIndex={zIndex}
        style={isMobile ? {} : floatingStyles}
        {...(enableHoverInteraction ? getFloatingProps() : {})}
        {...cardProps}
      >
        {children}
      </Card>
    );

    const renderWrapper = (state: TransitionStatus) => {
      if (isMobile) {
        return <MobileBackground isOpen={isOpen}>{renderCard(state, props)}</MobileBackground>;
      }

      return renderCard(state, { $fixedWidth: fixedWidth, $maxWidth: maxWidth, ...props });
    };

    // Get reference props for the trigger if hover interaction is enabled
    const referenceProps = enableHoverInteraction ? getReferenceProps() : {};

    // Determine which trigger ref to use
    const currentTriggerRef = internalTriggerRef.current ?? triggerRef;

    return (
      <>
        {renderTrigger?.({ ref: internalTriggerRef, ...referenceProps })}

        <Transition
          in={isOpen}
          timeout={CARD_OPENING_TRANSITION_DURATION}
          nodeRef={transitionNodeRef}
          unmountOnExit={unmountOnExit}
          appear={appear}
        >
          {(state) => (
            <ManagedLayer
              isOpen={isEnter(state) || isExiting(state)}
              onClose={onClose}
              wrapperRef={{ current: refs.floating.current }}
              triggerRef={{ current: currentTriggerRef ?? null }}
              additionalRefs={additionalRefs}
            >
              <Portal>{renderWrapper(state)}</Portal>
            </ManagedLayer>
          )}
        </Transition>
      </>
    );
  },
);

DropdownCard.displayName = 'DropdownCard';

export default DropdownCard;
